// Iter92 — One-click Shopify Merge Button.
// Lives next to "Refresh" on Admin Product Management.
//
// Flow:
//   1. Check credential status. If incomplete → open Credentials modal.
//   2. Show Confirm modal.
//   3. Stream the merge via POST /api/admin/shopify/one-click-merge.
//   4. Show result panel: created/updated/published/checkout-ready, webhook
//      status, cartCreate verification, errors.
//
// IMPORTANT: tokens are never stored in localStorage and never echoed
// back from the backend in plain form. The modal only sends user input
// to the backend save endpoint.
import React, { useCallback, useEffect, useState } from "react";
import {
  Wrench, KeyRound, X, Loader2, CheckCircle2, AlertTriangle,
  ExternalLink, Copy,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const REQUIRED_KEYS = [
  // Iter94 — Dev Dashboard apps. Only Shop Domain + Client ID + Client
  // Secret are required. Backend exchanges them for the Admin API
  // access token automatically (client_credentials OAuth grant).
  // Iter96 — Loud warning: operators kept pasting the admin.shopify.com
  // settings URL (e.g. https://admin.shopify.com/store/liftedatthemoment)
  // which Shopify's token endpoint rejects with an HTML 400 dump.
  { k: "SHOPIFY_SHOP_DOMAIN",            label: "Shopify Shop Domain",
    placeholder: "liftedatthemoment.myshopify.com",
    required: true,
    helper: "Paste your *.myshopify.com domain — NOT the admin.shopify.com settings URL. " +
             "Find it in Shopify Admin → Settings → Domains → 'Shopify subdomain'." },
  { k: "SHOPIFY_CLIENT_ID",              label: "Shopify Client ID (API Key)",
    placeholder: "abcd1234...",
    required: true, helper: "Dev Dashboard → App → Settings → API credentials" },
  { k: "SHOPIFY_CLIENT_SECRET",          label: "Shopify Client Secret",
    placeholder: "shpss_...",
    required: true, helper: "Dev Dashboard → App → Settings → API credentials" },
  // Optional fields.
  { k: "SHOPIFY_STOREFRONT_ACCESS_TOKEN", label: "Storefront Access Token (optional)",
    placeholder: "Only required for customer cartCreate / checkout",
    required: false, helper: "Optional — enables Storefront cartCreate smoke test" },
  { k: "SHOPIFY_WEBHOOK_SECRET",          label: "Webhook Secret (optional)",
    placeholder: "Used to verify Shopify webhook HMAC signatures",
    required: false, helper: "Optional — generate any random string and paste it here + into Shopify webhook config" },
];

const PROGRESS_STEPS = [
  "Checking Shopify connection",
  "Creating missing products",
  "Creating variants",
  "Publishing products",
  "Verifying Storefront checkout",
  "Saving Shopify IDs",
  "Setting up webhooks",
  "Complete",
];


export default function ShopifyMergeButton({ onMergeComplete }) {
  const [credStatus, setCredStatus]   = useState(null);
  const [showCreds, setShowCreds]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [creds, setCreds]             = useState({});
  const [savingCreds, setSavingCreds] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [connResult, setConnResult]   = useState(null);
  const [merging, setMerging]         = useState(false);
  const [step, setStep]               = useState(-1);
  const [result, setResult]           = useState(null);
  const [err, setErr]                 = useState("");
  const [copyOk, setCopyOk]           = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/shopify/credentials-status");
      setCredStatus(data);
    } catch (e) {
      setCredStatus(null);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  function openButton() {
    setErr(""); setResult(null); setConnResult(null);
    if (!credStatus || !credStatus.ready) {
      setShowCreds(true);
    } else {
      // Iter95 — Always pop the creds modal so the operator can run
      // Test Connection. They can hit "Continue to merge →" without
      // re-saving if creds are already configured.
      setShowCreds(true);
    }
  }

  async function saveCreds() {
    setSavingCreds(true); setErr(""); setConnResult(null);
    try {
      const payload = {};
      for (const { k } of REQUIRED_KEYS) if (creds[k]) payload[k] = creds[k];
      const { data } = await api.post("/admin/shopify/save-credentials", payload);
      // Clear plaintext from local component state immediately
      setCreds({});
      await loadStatus();
      // Iter95 — Don't auto-advance to confirm. Show the result inline
      // so the operator can run Test Connection BEFORE committing.
      if (data?.token_exchange?.ok) {
        setConnResult({ ok: true, fromSave: true, ...(data.token_exchange) });
      } else {
        setConnResult({ ok: false, fromSave: true,
                         errors: [{ step: "token_exchange",
                                     detail: data?.token_exchange?.error }],
                         hint: "Click 'Test Connection' to see the exact Shopify error." });
      }
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setSavingCreds(false); }
  }

  async function testConnection() {
    setTestingConn(true); setErr(""); setConnResult(null);
    try {
      const { data } = await api.post("/admin/shopify/test-connection", {});
      setConnResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setConnResult({ ok: false,
                       errors: [{ step: "request_error",
                                   detail: detail || apiErrorMessage(e) }] });
    } finally { setTestingConn(false); }
  }

  // Iter97 — Reset Connection. Wipes saved Shopify creds + tokens both
  // in the DB and in this worker's env. Also clears local state so the
  // stale masked domain no longer haunts the operator's modal.
  async function clearCreds() {
    if (!window.confirm(
        "This will delete the saved Shopify shop domain, client ID, "
      + "client secret, and generated Admin token from the server. "
      + "You'll need to re-enter them. Continue?"
    )) return;
    setSavingCreds(true); setErr(""); setConnResult(null);
    try {
      await api.post("/admin/shopify/clear-credentials", {});
      setCreds({});
      await loadStatus();
      setConnResult({ ok: false, cleared: true,
                       hint: "Saved credentials cleared. Enter your correct "
                              + "*.myshopify.com domain + Client ID + Secret "
                              + "and click Save credentials." });
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setSavingCreds(false); }
  }

  function proceedToConfirm() {
    setShowCreds(false);
    setShowConfirm(true);
  }

  async function runMerge() {
    setShowConfirm(false);
    setMerging(true); setStep(0); setErr(""); setResult(null);
    // Animate the progress labels so the user sees feedback even
    // though the actual call is a single POST. Backend reports the
    // detailed `steps[]` log in the response which we render below.
    const interval = setInterval(() => {
      setStep((s) => (s < PROGRESS_STEPS.length - 2 ? s + 1 : s));
    }, 1400);
    try {
      const { data } = await api.post("/admin/shopify/one-click-merge", {});
      setResult(data);
      setStep(PROGRESS_STEPS.length - 1);
      if (onMergeComplete) onMergeComplete();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      clearInterval(interval);
      setMerging(false);
    }
  }

  function copyWebhook() {
    try {
      navigator.clipboard.writeText("https://liftedatthemoment.com/api/webhooks/shopify");
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1500);
    } catch (e) { /* ignore */ }
  }

  return (
    <>
      <button
        onClick={openButton}
        disabled={merging}
        className="px-3 py-1.5 rounded text-xs font-bebas tracking-[0.18em] uppercase
                   bg-lifted-green/10 text-lifted-green border border-lifted-green/40
                   hover:bg-lifted-green/20 disabled:opacity-50 inline-flex items-center gap-1"
        data-testid="shopify-merge-btn"
      >
        {merging ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
        Sync / Merge Shopify
      </button>

      {/* Credentials modal — Iter98: scrollable body + sticky footer so
          the Continue-to-Merge button is always reachable on iPad /
          tablet / mobile Safari viewports. */}
      {showCreds && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 overflow-hidden"
              data-testid="shopify-creds-modal">
          <div className="w-full max-w-xl bg-lifted-black border border-lifted-green/30 rounded-lg flex flex-col max-h-[90vh]">
            {/* Header — static */}
            <div className="flex items-start justify-between px-6 pt-6 pb-3 shrink-0">
              <h3 className="font-anton uppercase text-xl text-white flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-lifted-green" /> Connect Shopify
              </h3>
              <button onClick={() => setShowCreds(false)} className="text-lifted-dim hover:text-white"
                       data-testid="shopify-creds-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Body — scrolls */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-4 space-y-4"
                  style={{ WebkitOverflowScrolling: "touch" }}
                  data-testid="shopify-creds-modal-body">
            <p className="text-xs text-lifted-dim">
              Shopify connection is not installed yet. Enter Shopify credentials to save securely.
              They are encrypted server-side and never sent back to your browser in plain text.
            </p>
            <div className="text-xs text-lifted-green/90 border border-lifted-green/30 rounded p-2 bg-lifted-green/5"
                  data-testid="shopify-creds-helper">
              <strong>Shopify Dev Dashboard apps</strong> use Client ID + Client Secret to generate the
              Admin API access token automatically. You do <strong>not</strong> need to paste an
              Admin token — the backend will exchange your credentials for one as soon as you save.
            </div>
            {/* Iter96 — Prominent domain-format warning. Blocks the most
                common operator error: pasting the admin.shopify.com URL. */}
            <div className="text-xs text-amber-300 border border-amber-400/40 rounded p-2 bg-amber-400/5 space-y-1"
                  data-testid="shopify-creds-domain-warning">
              <div className="font-bebas tracking-[0.18em] uppercase text-amber-300">
                ⚠ Use your <span className="font-mono">*.myshopify.com</span> domain — not the admin URL
              </div>
              <div className="text-lifted-dim">
                ✅ Correct: <code className="text-white">liftedatthemoment.myshopify.com</code><br/>
                ❌ Wrong: <code className="text-rose-300 line-through">https://admin.shopify.com/store/liftedatthemoment/…</code>
              </div>
              <div className="text-lifted-dim">
                To find it: <span className="text-white">Shopify Admin → Settings → Domains → &ldquo;Shopify subdomain&rdquo;</span>.
              </div>
            </div>
            {/* Iter97 — Show the operator EXACTLY what shop domain is
                currently persisted server-side. Not a secret — helps
                diagnose the "app is still hitting the old domain" case. */}
            {credStatus?.shop_domain_value && (
              <div className="text-xs border border-white/10 rounded p-2 bg-black/30 flex items-center justify-between gap-2"
                    data-testid="shopify-saved-domain">
                <div>
                  <span className="text-lifted-dim">Currently saved shop domain: </span>
                  <code className="text-white font-mono">{credStatus.shop_domain_value}</code>
                </div>
                <button onClick={clearCreds}
                         disabled={savingCreds || testingConn}
                         className="text-[0.6rem] uppercase tracking-[0.18em] text-rose-300 hover:text-rose-200 disabled:opacity-40"
                         data-testid="shopify-clear-creds-btn">
                  Clear &amp; Reset
                </button>
              </div>
            )}
            {REQUIRED_KEYS.map(({ k, label, placeholder, required, helper }) => {
              const cur = (credStatus || {})[k] || {};
              return (
                <div key={k} className="space-y-1">
                  <label className="text-[0.65rem] uppercase tracking-[0.18em] text-lifted-dim">
                    {label}{required && <span className="text-rose-300 ml-1">*</span>}
                    {cur.present && (
                      <span className="ml-2 text-lifted-green">
                        currently set ({cur.masked})
                      </span>
                    )}
                  </label>
                  <input
                    type={k === "SHOPIFY_SHOP_DOMAIN" ? "text" : "password"}
                    autoComplete="new-password"
                    value={creds[k] || ""}
                    placeholder={cur.present ? "Leave blank to keep" : placeholder}
                    onChange={(e) => setCreds(c => ({ ...c, [k]: e.target.value }))}
                    className="input-lifted w-full text-sm"
                    data-testid={`shopify-cred-${k.toLowerCase()}`}
                  />
                  {/* Iter97 — Live typo hint. If the operator types
                      `foo.shopify.com` (missing `my`), tell them
                      immediately — do NOT wait for a Save round-trip. */}
                  {k === "SHOPIFY_SHOP_DOMAIN" && (() => {
                    const v = String(creds[k] || "").toLowerCase().trim()
                                  .replace(/^https?:\/\//, "").split("/")[0];
                    if (!v) return null;
                    if (v.endsWith(".myshopify.com")) return null;
                    if (v.startsWith("admin.shopify.com")) {
                      return (
                        <div className="text-[0.65rem] text-rose-300"
                              data-testid="shopify-cred-domain-hint-admin">
                          ✗ That&rsquo;s the Shopify Admin URL — use your
                          <span className="text-white font-mono"> *.myshopify.com </span>
                          domain instead.
                        </div>
                      );
                    }
                    if (v.endsWith(".shopify.com")) {
                      const handle = v.slice(0, -".shopify.com".length);
                      return (
                        <div className="text-[0.65rem] text-amber-300"
                              data-testid="shopify-cred-domain-hint-typo">
                          Shopify uses <span className="font-mono">.myshopify.com</span>,
                          not <span className="font-mono">.shopify.com</span>.
                          Did you mean <code className="text-white">{handle}.myshopify.com</code>?
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {helper && <div className="text-[0.6rem] text-lifted-dim">{helper}</div>}
                </div>
              );
            })}
            {err && (
              <div className="text-xs text-rose-300 border border-rose-500/40 bg-rose-500/10 p-2 rounded"
                    data-testid="shopify-creds-error">{err}</div>
            )}

            {/* Iter95 — Connection test result. Shows the exact Shopify
                error so the operator can fix the source issue before
                committing to a full merge. */}
            {connResult && (
              <div className={`text-xs border rounded p-2 space-y-1 ${
                  connResult.ok
                    ? "border-lifted-green/40 bg-lifted-green/5 text-lifted-green"
                    : "border-rose-500/40 bg-rose-500/10 text-rose-300"
                }`} data-testid="shopify-test-conn-result">
                <div className="font-bebas tracking-[0.18em] uppercase">
                  {connResult.ok
                     ? "✓ Shopify connection verified"
                     : "✗ Shopify connection failed"}
                </div>
                {connResult.shop_domain && (
                  <div className="text-lifted-dim">
                    Shop: <span className="text-white font-mono">{connResult.shop_domain}</span>
                  </div>
                )}
                {connResult.shopify_shop && (
                  <div className="text-lifted-dim">
                    Verified Shopify shop: <span className="text-white">
                      {connResult.shopify_shop.name}</span>
                  </div>
                )}
                {connResult.granted_scopes?.length > 0 && (
                  <div className="text-lifted-dim">
                    Granted scopes ({connResult.granted_scopes.length}):
                    <span className="text-white font-mono ml-1">
                      {connResult.granted_scopes.join(", ")}
                    </span>
                  </div>
                )}
                {connResult.missing_scopes?.length > 0 && (
                  <div className="text-amber-300">
                    Missing required scopes: {connResult.missing_scopes.join(", ")}
                  </div>
                )}
                {/* Iter96 — Prefer the short error code + hint over
                    Shopify's raw HTML 400 dump. */}
                {connResult.error_code && (
                  <div className="text-rose-300 font-mono uppercase tracking-[0.14em] text-[0.65rem]"
                        data-testid="shopify-test-conn-error-code">
                    error: {connResult.error_code}
                  </div>
                )}
                {(connResult.errors || []).map((e, i) => {
                  // Iter96 — Extract the short code from the detail
                  // payload; suppress the raw HTML if we already
                  // surfaced a code above.
                  const short = e.code
                    || (e.detail && typeof e.detail === "object"
                          ? (e.detail.code || e.detail.message)
                          : null);
                  const raw = typeof e.detail === "object"
                    ? JSON.stringify(e.detail)
                    : String(e.detail || "");
                  // Hide the raw HTML dump when it's clearly a Shopify
                  // error page (starts with '<' or contains '<html').
                  const isHtml = raw.trimStart().startsWith("<")
                                    || raw.toLowerCase().includes("<html");
                  return (
                    <div key={i} className="text-rose-300 font-mono break-all text-[0.65rem]">
                      [{e.step}]{" "}
                      {short
                        ? <span className="text-white">{short}</span>
                        : (isHtml
                             ? <span className="text-lifted-dim italic">(Shopify returned an HTML error page — see hint below)</span>
                             : raw.slice(0, 240))}
                    </div>
                  );
                })}
                {connResult.hint && (
                  <div className="text-amber-300 mt-1">{connResult.hint}</div>
                )}
                {/* Iter97 — Domain-consistency diagnostics. Renders the
                    exact shop_domain seen at each layer so the operator
                    can prove they're not silently hitting a stale
                    saved value. */}
                {connResult.diagnostics && (
                  <div className="mt-2 pt-2 border-t border-white/10 space-y-0.5"
                        data-testid="shopify-domain-diagnostics">
                    <div className="text-[0.6rem] uppercase tracking-[0.18em] text-lifted-dim">
                      Domain diagnostics
                    </div>
                    <div className="text-lifted-dim font-mono text-[0.65rem]">
                      typed: <span className="text-white">
                        {(creds.SHOPIFY_SHOP_DOMAIN || "(unchanged)")}
                      </span>
                    </div>
                    <div className="text-lifted-dim font-mono text-[0.65rem]">
                      saved (DB): <span className="text-white">
                        {connResult.diagnostics.saved_shop_domain || "(none)"}
                      </span>
                    </div>
                    <div className="text-lifted-dim font-mono text-[0.65rem]">
                      env (worker): <span className="text-white">
                        {connResult.diagnostics.env_shop_domain || "(none)"}
                      </span>
                    </div>
                    <div className="text-lifted-dim font-mono text-[0.65rem]">
                      token exchange: <span className="text-white">
                        {connResult.diagnostics.token_exchange_shop_domain || "(none)"}
                      </span>
                    </div>
                    <div className={`font-mono text-[0.65rem] ${
                      connResult.diagnostics.domains_consistent
                        ? "text-lifted-green" : "text-rose-300"
                    }`} data-testid="shopify-domain-consistency">
                      {connResult.diagnostics.domains_consistent
                        ? "✓ All layers agree on the same shop domain"
                        : "✗ Domain mismatch — click Clear & Reset, then re-enter and Save."}
                    </div>
                  </div>
                )}
              </div>
            )}

            </div> {/* /body */}

            {/* Footer — sticky, always reachable */}
            <div className="shrink-0 border-t border-white/10 bg-lifted-black px-6 py-3 rounded-b-lg"
                  data-testid="shopify-creds-modal-footer">
              <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => setShowCreds(false)}
                       className="btn-lifted-outline text-xs"
                       data-testid="shopify-creds-cancel">
                Cancel
              </button>
              <button onClick={saveCreds}
                       disabled={savingCreds || testingConn}
                       className="btn-lifted-outline text-xs inline-flex items-center gap-1 disabled:opacity-50"
                       data-testid="shopify-creds-save">
                {savingCreds ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Save credentials
              </button>
              <button onClick={testConnection}
                       disabled={testingConn || savingCreds}
                       className="btn-lifted-outline text-xs inline-flex items-center gap-1 disabled:opacity-50 border-lifted-green/60 text-lifted-green"
                       data-testid="shopify-test-connection-btn">
                {testingConn ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Test Connection
              </button>
              {(() => {
                // Iter97 — Continue-to-Merge gate. All three must hold:
                //   • Test Connection returned ok=true
                //   • Backend reports all layers agree on the domain
                //   • Operator has NOT typed a new domain that differs
                //     from the saved domain (would need re-save first)
                const savedDomain = connResult?.diagnostics?.saved_shop_domain
                                        || credStatus?.shop_domain_value
                                        || "";
                const typed = String(creds.SHOPIFY_SHOP_DOMAIN || "")
                                  .trim().toLowerCase()
                                  .replace(/^https?:\/\//, "").split("/")[0];
                const typedDiffers = !!typed && typed !== savedDomain;
                const domainsAgree = connResult?.diagnostics
                    ? connResult.diagnostics.domains_consistent
                    : true;  // legacy back-end response — trust `.ok`
                const gate = connResult?.ok && domainsAgree && !typedDiffers;
                const gateReason = !connResult?.ok
                  ? "Run Test Connection first (must return ✓)"
                  : (typedDiffers
                      ? "You typed a new domain — click Save credentials to persist it, then re-run Test Connection."
                      : (!domainsAgree
                          ? "Backend reports a domain mismatch — click Clear & Reset."
                          : "Proceed to merge"));
                return (
                  <button onClick={proceedToConfirm}
                           disabled={!gate}
                           title={gateReason}
                           className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-40"
                           data-testid="shopify-proceed-to-merge">
                    Continue to merge →
                  </button>
                );
              })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
              data-testid="shopify-confirm-modal">
          <div className="w-full max-w-md bg-lifted-black border border-amber-400/40 rounded-lg p-6 space-y-3">
            <h3 className="font-anton uppercase text-xl text-white">Confirm Shopify Merge</h3>
            <p className="text-xs text-lifted-dim">
              This will sync all Lifted At The Moment products, variants, inventory,
              images, and checkout IDs to Shopify. Continue?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowConfirm(false)}
                       className="btn-lifted-outline text-xs"
                       data-testid="shopify-confirm-cancel">
                Cancel
              </button>
              <button onClick={runMerge}
                       className="btn-lifted text-xs inline-flex items-center gap-1"
                       data-testid="shopify-confirm-run">
                <Wrench className="h-3 w-3" /> Run Shopify Merge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress / result */}
      {(merging || result) && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
              data-testid="shopify-merge-status">
          <div className="w-full max-w-3xl bg-lifted-black border border-lifted-green/30 rounded-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <h3 className="font-anton uppercase text-2xl text-white">
                {merging ? "Running Shopify Merge…" : "Shopify Merge complete"}
              </h3>
              {!merging && (
                <button onClick={() => { setResult(null); setStep(-1); }}
                         className="text-lifted-dim hover:text-white"
                         data-testid="shopify-merge-close">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Progress steps */}
            <ol className="space-y-1 text-xs" data-testid="shopify-merge-progress">
              {PROGRESS_STEPS.map((label, i) => {
                const state = i < step ? "done" : i === step ? "active" : "pending";
                return (
                  <li key={i}
                       className={`flex items-center gap-2 ${
                         state === "done" ? "text-lifted-green"
                         : state === "active" ? "text-white"
                         : "text-lifted-dim"
                       }`}
                       data-testid={`shopify-merge-step-${i}`}>
                    {state === "done"
                       ? <CheckCircle2 className="h-3 w-3" />
                       : state === "active"
                         ? <Loader2 className="h-3 w-3 animate-spin" />
                         : <span className="h-3 w-3 rounded-full border border-current opacity-40" />}
                    {label}
                  </li>
                );
              })}
            </ol>

            {err && (
              <div className="text-xs text-rose-300 border border-rose-500/40 bg-rose-500/10 p-2 rounded">
                {err}
              </div>
            )}

            {result && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
                      data-testid="shopify-merge-result">
                  {[
                    ["Scanned",          result.scanned_products],
                    ["Created",          result.created_products],
                    ["Updated",          result.updated_products],
                    ["Variants created", result.created_variants],
                    ["Published",        result.published_products],
                    ["Checkout-ready",   result.checkout_ready],
                    ["Storefront-visible", result.storefront_visible],
                    ["Still failing",    result.still_unready],
                  ].map(([label, value]) => (
                    <div key={label}
                         className="border border-white/10 rounded p-2 bg-black/30">
                      <div className="text-[0.55rem] uppercase tracking-[0.18em] text-lifted-dim">{label}</div>
                      <div className="text-white font-anton text-lg">{value ?? 0}</div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-lifted-dim flex flex-wrap items-center gap-2">
                  <span className={`px-2 py-0.5 rounded ${
                    result.connection_ready ? "bg-lifted-green/20 text-lifted-green" : "bg-rose-500/20 text-rose-300"
                  }`} data-testid="shopify-merge-conn-status">
                    Connection: {result.connection_ready ? "OK" : "FAILED"}
                  </span>
                  <span className={`px-2 py-0.5 rounded ${
                    result.cartcreate_verified ? "bg-lifted-green/20 text-lifted-green" : "bg-amber-500/20 text-amber-300"
                  }`} data-testid="shopify-merge-cart-status">
                    cartCreate: {result.cartcreate_verified ? "VERIFIED" : "NOT VERIFIED"}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-white/5"
                         data-testid="shopify-merge-readiness">
                    Strict readiness: {(result.readiness_pct ?? 0).toFixed(1)}%
                  </span>
                </div>

                {result.cartcreate_sample && result.cartcreate_verified && (
                  <div className="text-xs text-lifted-dim border border-lifted-green/20 rounded p-2 bg-lifted-green/5"
                        data-testid="shopify-merge-cart-sample">
                    Smoke test: <span className="text-white">{result.cartcreate_sample.title}</span> →
                    <a href={result.cartcreate_sample.checkout_url}
                        target="_blank" rel="noopener noreferrer"
                        className="text-lifted-green ml-1 inline-flex items-center gap-1">
                      Shopify checkoutUrl <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}

                {/* Webhook setup — always show callback URL + copy
                    affordance once the merge has run, even when the
                    connection failed before reaching the webhook step. */}
                {result.webhooks && (
                  <div className="text-xs space-y-1" data-testid="shopify-merge-webhooks">
                    <div className="text-lifted-dim uppercase tracking-[0.18em] text-[0.6rem]">Webhooks</div>
                    <div className="border border-white/10 rounded p-2 bg-black/30 space-y-1">
                      <div className="text-lifted-dim">
                        Required topics: <span className="text-white font-mono">
                          {(result.webhooks.required || []).join(", ")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 font-mono text-white">
                        <code data-testid="shopify-webhook-url">{result.webhooks.callback_url}</code>
                        <button onClick={copyWebhook}
                                 className="text-lifted-green hover:text-white inline-flex items-center gap-1"
                                 data-testid="shopify-webhook-copy">
                          {copyOk ? "Copied!" : <><Copy className="h-3 w-3" /> Copy</>}
                        </button>
                      </div>
                    </div>
                    {(result.webhooks.created || []).length > 0 && (
                      <div className="text-lifted-green">
                        ✓ Auto-created: {result.webhooks.created.join(", ")}
                      </div>
                    )}
                    {(result.webhooks.already_existed || []).length > 0 && (
                      <div className="text-lifted-dim">
                        ↻ Already configured: {result.webhooks.already_existed.join(", ")}
                      </div>
                    )}
                    {(result.webhooks.manual_setup_required || []).length > 0 && (
                      <div className="border border-amber-400/40 bg-amber-400/5 rounded p-2 space-y-1">
                        <div className="text-amber-300 font-medium">
                          Manual webhook setup required: {result.webhooks.manual_setup_required.join(", ")}
                        </div>
                        <div className="text-lifted-dim">
                          1. Open Shopify Admin → Settings → Notifications → Webhooks<br/>
                          2. Add a webhook for each topic above pointing to the URL above<br/>
                          3. Use the same SHOPIFY_WEBHOOK_SECRET so HMAC verification passes.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(result.errors || []).length > 0 && (
                  <details className="text-xs text-rose-300" data-testid="shopify-merge-errors">
                    <summary className="cursor-pointer">
                      {(result.errors || []).length} error(s) — click to expand
                    </summary>
                    <ul className="mt-1 space-y-1 max-h-48 overflow-y-auto font-mono">
                      {(result.errors || []).slice(0, 50).map((e, i) => (
                        <li key={i}>
                          [{e.handle || e.step || "?"}]{" "}
                          {typeof e.detail === "object"
                            ? JSON.stringify(e.detail).slice(0, 240)
                            : String(e.detail || e.error || e.userErrors || "").slice(0, 240)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* Step log */}
                {result.steps && (
                  <details className="text-xs text-lifted-dim" data-testid="shopify-merge-logs">
                    <summary className="cursor-pointer">View step log ({result.steps.length})</summary>
                    <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto font-mono">
                      {result.steps.map((s, i) => (
                        <li key={i} className={s.ok ? "text-lifted-dim" : "text-rose-300"}>
                          {s.step}: {s.ok ? "OK" : "FAIL"} {Object.entries(s)
                              .filter(([k]) => !["step", "ok", "ts"].includes(k))
                              .map(([k, v]) => `${k}=${v}`).join(" ")}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className="pt-2 border-t border-white/5">
                  {result.success ? (
                    <div className="text-lifted-green text-sm inline-flex items-center gap-2"
                          data-testid="shopify-merge-success-banner">
                      <CheckCircle2 className="h-4 w-4" /> Shopify is ready for checkout.
                    </div>
                  ) : (
                    <div className="text-amber-300 text-sm inline-flex items-center gap-2"
                          data-testid="shopify-merge-warning-banner">
                      <AlertTriangle className="h-4 w-4" /> Merge finished with issues. Review errors above.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
